// /home/lucabrugaletta/Desktop/Works council/frontend/src/pages/Login.js
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import GDPRConsentModal from '../components/GDPRConsentModal';
import axios from 'axios';
import '../styles/shared.css';

const Login = () => {
  const navigate = useNavigate();
  const { login, submitGdprConsent } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGdprModal, setShowGdprModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await login(email, password);
      
      if (result.requiresGdprConsent) {
        // Show GDPR consent modal
        setUserData(result.userData);
        setShowGdprModal(true);
        setLoading(false);
      } else {
        // User has already accepted GDPR, proceed to dashboard
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      setLoading(false);
    }
  };
  
  const handleGdprAccept = async () => {
    try {
      setLoading(true);
      
      // Use the AuthContext's submitGdprConsent function
      await submitGdprConsent(true, userData);
      
      // Hide modal and navigate to dashboard
      setShowGdprModal(false);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to submit GDPR consent: ' + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };
  
  const handleGdprDecline = () => {
    // User declined GDPR consent
    setShowGdprModal(false);
    setError('You must accept the GDPR consent to use this application');
  };
  
  const handleResetPassword = async () => {
    setError('');
    
    if (!resetEmail || !resetEmail.trim()) {
      setError('Please enter an email address');
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the backend API to initiate password reset
      await axios.post('/api/auth/forgot-password', { email: resetEmail });
      setResetSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetModalClose = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetSuccess(false);
    setError('');
  };

  return (
    <div className="centered-page">
      {/* GDPR Consent Modal */}
      <GDPRConsentModal 
        open={showGdprModal}
        onAccept={handleGdprAccept}
        onDecline={handleGdprDecline}
        userData={userData}
      />
      
      {/* Password Reset Modal */}
      <Dialog open={showResetModal} onClose={handleResetModalClose} maxWidth="sm" fullWidth
        BackdropProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(0,153,153,0.10), rgba(102,204,204,0.10))',
            backdropFilter: 'blur(2px)'
          }
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--light-bg)'
          }
        }}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              If the email exists, a password reset link has been sent.
            </Alert>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                className="mui-input form-field"
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {resetSuccess ? (
            <Button onClick={handleResetModalClose} variant="contained" color="primary">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleResetModalClose} disabled={loading}>
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleResetPassword} 
                variant="contained" 
                color="primary" 
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Send Reset Email"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={4}
        className="glass-paper form-container"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <img 
            src="/logo.svg" // Black logo for light theme 
            alt="Works Council Logo" 
            className="logo"
            style={{ width: '90px' }}
          />
          <Typography variant="h4" component="h1" align="center" className="text-primary" sx={{ fontWeight: 600 }}>
            Login
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} autoComplete="off" noValidate>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mui-input form-field"
          />
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mui-input form-field"
          />
          
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            className="btn-primary"
            sx={{ mt: 2, mb: 2, py: 1.5 }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Log In"}
          </Button>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
              <MuiLink 
                component="button" 
                type="button"
                variant="body2" 
                onClick={(e) => {
                  e.preventDefault();
                  setShowResetModal(true);
                  setError('');
                }}
                sx={{ color: '#99D6D6' }}
              >
                Forgot Password?
              </MuiLink>
          </Box>
        </Box>
      </Paper>
    </div>
  );
};

export default Login;